import React, { useState, useEffect, useCallback, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-jalaali';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PersianDatePicker from '../components/PersianDatePicker';
import { getApiBaseUrl } from '../utils/getApiUrl';
import { canAccessManagementReport } from '../utils/managementReportAccess';
import type { Appointment } from '../types';

/** ارقام لاتین رشتهٔ تاریخ/زمان را به فارسی (برای نمایش یکدست با کارت‌های گزارش) */
const toPersianDigits = (s: string): string =>
  s.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

/**
 * تاریخ روز میلادی برگشتی از سرور (مثلاً YYYY-MM-DD) را به شمسی برمی‌گرداند.
 */
const gregorianDayToJalaliLabel = (dayStr: string): string => {
  if (!dayStr || !dayStr.trim()) return '—';
  const trimmed = dayStr.trim().slice(0, 10);
  // moment-jalaali در تایپ پروژه فقط یک آرگومان می‌پذیرد؛ نیمه‌روز برای جلوگیری از جابه‌جایی روز با تایم‌زون
  const m =
    /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
      ? moment(new Date(`${trimmed}T12:00:00`))
      : moment(dayStr);
  if (!m.isValid()) return dayStr;
  return toPersianDigits(m.format('jYYYY/jMM/jDD'));
};

/** برچسب کوتاه محور نمودار روزانه (بدون سال) */
const gregorianDayToJalaliChartLabel = (dayStr: string): string => {
  if (!dayStr || !dayStr.trim()) return '—';
  const trimmed = dayStr.trim().slice(0, 10);
  const m =
    /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
      ? moment(new Date(`${trimmed}T12:00:00`))
      : moment(dayStr);
  if (!m.isValid()) return dayStr;
  return toPersianDigits(m.format('jMM/jDD'));
};

/** تاریخ شمسی jYYYY/jMM/jDD → میلادی YYYY-MM-DD برای پارامتر API */
const jalaliToGregorianYmd = (jalali: string): string | null => {
  const parts = jalali.split('/');
  if (parts.length !== 3) return null;
  const jy = parseInt(parts[0], 10);
  const jm = parseInt(parts[1], 10);
  const jd = parseInt(parts[2], 10);
  if (Number.isNaN(jy) || Number.isNaN(jm) || Number.isNaN(jd)) return null;
  const m = moment().jYear(jy).jMonth(jm - 1).jDate(jd);
  if (!m.isValid()) return null;
  return m.format('YYYY-MM-DD');
};

const adminRoleFa = (role: string): string => {
  const r = (role || '').toLowerCase().trim();
  if (r === 'admin') return 'مدیر کل';
  if (r === 'manager') return 'مدیر';
  if (r === 'expert') return 'کارشناس';
  return role || '—';
};

interface ExpertRankingActivity {
  admin_id: number;
  username: string;
  role: string;
  total_actions: number;
  distinct_appointments_touched: number;
  /** سهم از مجموع اقدامات ثبت‌شده در بازه (میان همهٔ کاربران دارای لاگ) */
  actions_share_pct: number;
}

interface ExpertRankingCancel {
  admin_id: number;
  username: string;
  role: string;
  distinct_appointments_touched: number;
  cancellations: number;
  cancellation_pct: number;
  cancellations_on_repeat_appointments: number;
}

interface ExpertRankingComplete {
  admin_id: number;
  username: string;
  role: string;
  distinct_appointments_touched: number;
  completions: number;
  completion_pct: number;
}

interface ExpertInsights {
  rankings: {
    by_activity: ExpertRankingActivity[];
    by_cancellations: ExpertRankingCancel[];
    by_completions: ExpertRankingComplete[];
  };
}

interface ReportStaffOption {
  admin_id: number;
  username: string;
  role: string;
}

interface ManagementSummary {
  period_start: string;
  period_end: string;
  days: number;
  filtered_admin?: {
    admin_id: number;
    username: string;
    role: string;
  } | null;
  appointments: {
    total_registered: number;
    incomplete: number;
    in_queue: number;
    completed: number;
    cancelled: number;
    in_progress_total: number;
  };
  repeat_appointments_same_name: number;
  /** جمع «در انتظار» + «در حال انجام» در همان بازهٔ ثبت */
  open_orders: number;
  by_service: { service_id: number | null; service_name: string; count: number }[];
  by_day: {
    date: string;
    count: number;
    completed: number;
    cancelled: number;
    open: number;
  }[];
  expert_insights?: ExpertInsights;
}

/** رنگ برش‌های نمودار دایره‌ای خدمت (هگز هماهنگ با تم قبلی) */
const SERVICE_PIE_FILLS = [
  '#3b82f6',
  '#6366f1',
  '#14b8a6',
  '#f59e0b',
  '#f43f5e',
  '#8b5cf6',
  '#10b981',
  '#f97316',
] as const;

/** مسیر یک برش حلقه‌ای (دونات) از startRad به طول sweepRad رادیان */
const donutSlicePath = (
  cx: number,
  cy: number,
  rOut: number,
  rIn: number,
  startRad: number,
  sweepRad: number
): string => {
  if (sweepRad <= 1e-6) return '';
  const endRad = startRad + sweepRad;
  const large = sweepRad > Math.PI ? 1 : 0;
  const xo1 = cx + rOut * Math.cos(startRad);
  const yo1 = cy + rOut * Math.sin(startRad);
  const xo2 = cx + rOut * Math.cos(endRad);
  const yo2 = cy + rOut * Math.sin(endRad);
  const xi2 = cx + rIn * Math.cos(endRad);
  const yi2 = cy + rIn * Math.sin(endRad);
  const xi1 = cx + rIn * Math.cos(startRad);
  const yi1 = cy + rIn * Math.sin(startRad);
  return [
    `M ${xo1} ${yo1}`,
    `A ${rOut} ${rOut} 0 ${large} 1 ${xo2} ${yo2}`,
    `L ${xi2} ${yi2}`,
    `A ${rIn} ${rIn} 0 ${large} 0 ${xi1} ${yi1}`,
    'Z',
  ].join(' ');
};

/** ارتفاع ناحیهٔ میله‌ها در نمودار روزانه (پیکسل) */
const DAILY_CHART_BAR_AREA_PX = 160;
const DAILY_CHART_SCROLL_THRESHOLD = 14;
const DAILY_CHART_BAR_WIDTH_CLASS = 'w-8 sm:w-9';

const DAILY_CHART_SEGMENTS = [
  {
    key: 'completed' as const,
    label: 'تکمیل‌شده',
    barClass: 'bg-emerald-500',
  },
  {
    key: 'open' as const,
    label: 'سفارش باز',
    barClass: 'bg-amber-400',
  },
  {
    key: 'cancelled' as const,
    label: 'لغوشده',
    barClass: 'bg-rose-500',
  },
];

type DailyDayRow = ManagementSummary['by_day'][number];

const DailyBreakdownBar: React.FC<{
  day: DailyDayRow;
  maxDayCount: number;
  compact: boolean;
}> = ({ day, maxDayCount, compact }) => {
  const jLabelFull = gregorianDayToJalaliLabel(day.date);
  const jLabel = compact ? gregorianDayToJalaliChartLabel(day.date) : jLabelFull;
  const barH = Math.max(4, Math.round((day.count / maxDayCount) * DAILY_CHART_BAR_AREA_PX));
  const segments = DAILY_CHART_SEGMENTS.map((seg) => ({
    ...seg,
    count: day[seg.key] ?? 0,
  })).filter((seg) => seg.count > 0);
  const tooltipParts = segments
    .map((seg) => `${seg.label}: ${seg.count.toLocaleString('fa-IR')}`)
    .join(' · ');

  return (
    <div
      className={`flex flex-col items-center ${
        compact ? 'w-10 shrink-0 snap-start sm:w-11' : 'min-w-0 px-1'
      }`}
    >
      <div
        className="flex w-full flex-col items-center justify-end border-b border-gray-300/90"
        style={{ height: DAILY_CHART_BAR_AREA_PX }}
      >
        {day.count > 0 ? (
          <div
            className={`${DAILY_CHART_BAR_WIDTH_CLASS} flex flex-col justify-end overflow-hidden rounded-t-[4px]`}
            style={{ height: barH }}
            title={`${jLabelFull} — ${day.count.toLocaleString('fa-IR')} نوبت${tooltipParts ? ` (${tooltipParts})` : ''}`}
          >
            {(() => {
              let usedH = 0;
              return segments.map((seg, idx) => {
                const isLast = idx === segments.length - 1;
                const segH = isLast
                  ? Math.max(2, barH - usedH)
                  : Math.max(2, Math.round((seg.count / day.count) * barH));
                usedH += segH;
                return (
                  <div
                    key={seg.key}
                    className={`w-full ${seg.barClass}`}
                    style={{ height: segH }}
                    title={`${seg.label}: ${seg.count.toLocaleString('fa-IR')}`}
                  />
                );
              });
            })()}
          </div>
        ) : null}
      </div>
      <p
        className={`mt-2 w-full text-center text-[11px] leading-tight text-gray-600 ${
          compact ? 'whitespace-nowrap' : 'truncate'
        }`}
        title={compact ? jLabelFull : undefined}
      >
        {jLabel}
      </p>
      <p className="mt-0.5 text-sm font-semibold tabular-nums text-gray-900">
        {day.count.toLocaleString('fa-IR')}
      </p>
    </div>
  );
};

const defaultJalaliWeekRange = () => ({
  from: moment().subtract(6, 'days').format('jYYYY/jMM/jDD'),
  to: moment().format('jYYYY/jMM/jDD'),
});

const reportToolbarBtnClass =
  'inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 min-h-[46px] text-base font-semibold transition-all disabled:opacity-50 disabled:active:scale-100';

const toolbarTooltipBubbleClass =
  'pointer-events-none invisible absolute bottom-full left-1/2 z-30 mb-2 w-max max-w-[min(18rem,calc(100vw-2rem))] -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-center text-xs font-normal leading-relaxed text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100';

/** تولتیپ زیر المان؛ لبهٔ راست با والد هم‌تراز تا در RTL از صفحه بیرون نزند */
const toolbarTooltipBelowClass =
  'pointer-events-none invisible absolute top-full right-0 z-50 mt-2 w-max max-w-[min(28rem,calc(100vw-2rem))] rounded-lg bg-gray-900 px-3 py-2.5 text-right text-xs font-normal leading-relaxed text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100';

const EXPERT_INSIGHTS_HINT =
  'رتبه‌بندی کارشناسانی که در این بازه روی نوبت لاگ دارند. «٪ از کل اقدامات» سهم همان کاربر از جمع لاگ‌های اقدام در این بازه است. ستون «نوبت» تعداد نوبت‌های متمایزی است که همان کاربر در بازه روی آن‌ها اقدام ثبت کرده است. «تکمیل شده» و «لغو شده» تعداد و درصد نسبت به همان نوبت‌های متمایز است.';

const EXPERT_ACTIVITY_COLUMN_HINTS = {
  rank: 'ترتیب نمایش بر اساس فعالیت در این بازه؛ رتبه ۱ فعال‌ترین کارشناس است.',
  username: 'نام کاربری حساب ادمین در سامانه.',
  role: 'نقش دسترسی کاربر (کارشناس، مدیر، مدیر کل و …).',
  share: 'سهم این کارشناس از جمع کل لاگ‌های اقدام ثبت‌شده در همین بازه (میان کاربران این جدول).',
  appointments: 'تعداد نوبت‌های متمایزی که این کارشناس در بازه روی آن‌ها لاگ اقدام ثبت کرده است.',
  completed: 'تعداد تکمیل‌های ثبت‌شده توسط این کارشناس در بازه؛ درصد زیر آن نسبت به نوبت‌های متمایز همان کاربر است.',
  cancelled: 'تعداد لغوهای ثبت‌شده توسط این کارشناس در بازه؛ درصد زیر آن نسبت به نوبت‌های متمایز همان کاربر است.',
} as const;

const formatExpertPct = (n: number): string =>
  n.toLocaleString('fa-IR', { maximumFractionDigits: 1 });

const expertStatsByAdminId = (
  rankings: ExpertInsights['rankings'],
  adminId: number
): {
  completions: number;
  completionPct: number;
  cancellations: number;
  cancellationPct: number;
} => {
  const completeRow = rankings.by_completions.find((r) => r.admin_id === adminId);
  const cancelRow = rankings.by_cancellations.find((r) => r.admin_id === adminId);
  return {
    completions: completeRow?.completions ?? 0,
    completionPct: completeRow?.completion_pct ?? 0,
    cancellations: cancelRow?.cancellations ?? 0,
    cancellationPct: cancelRow?.cancellation_pct ?? 0,
  };
};

const tableHeaderTooltipClass =
  'pointer-events-none invisible absolute bottom-full left-1/2 z-40 mb-1.5 w-max max-w-[13rem] -translate-x-1/2 rounded-md bg-gray-900 px-2.5 py-2 text-center text-[11px] font-normal leading-relaxed text-white opacity-0 shadow-md transition-opacity duration-150 group-hover/th:visible group-hover/th:opacity-100 group-focus-within/th:visible group-focus-within/th:opacity-100';

const TooltipInfoIcon: React.FC = () => (
  <span
    className="inline-flex h-4 min-w-[1rem] shrink-0 select-none items-center justify-center rounded-full border border-gray-200 bg-gray-50 px-1 text-[8px] font-bold leading-none text-gray-500"
    aria-hidden
  >
    i
  </span>
);

const TableHeaderWithTooltip: React.FC<{
  label: string;
  hint: string;
  align?: 'left' | 'right' | 'center';
}> = ({ label, hint, align = 'right' }) => {
  const hintId = useId().replace(/:/g, '');
  const alignClass =
    align === 'left' ? 'text-left' : align === 'center' ? 'text-center' : 'text-right';
  return (
    <th
      className={`px-2 py-2.5 text-sm font-semibold text-gray-600 sm:px-3 sm:py-3 ${alignClass}`}
    >
      <span
        className="group/th relative inline-flex cursor-help items-center gap-1.5 outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 rounded"
        tabIndex={0}
        aria-describedby={hintId}
      >
        <span>{label}</span>
        <TooltipInfoIcon />
        <span id={hintId} className="sr-only">
          {hint}
        </span>
        <span role="tooltip" aria-hidden="true" className={tableHeaderTooltipClass}>
          {hint}
        </span>
      </span>
    </th>
  );
};

const EXCEL_EXPORT_HINT =
  'این فایل گزارش خام نوبت‌هاست (CSV)؛ همان بازهٔ تاریخ بالا را می‌دهد و در اکسل باز می‌شود.';

const CANCEL_REASONS_EXPORT_HINT =
  '«خروجی دلایل لغو» کل سابقه را می‌گیرد؛ به بازهٔ بالا وابسته نیست.';

type ReportCardFilter = 'total' | 'open' | 'completed' | 'cancelled';

const REPORT_CARD_LIST_PAGE_SIZE = 50;

const REPORT_CARD_CONFIG: Record<ReportCardFilter, { title: string }> = {
  total: { title: 'کل نوبت‌های ثبت‌شده' },
  open: { title: 'سفارشات باز' },
  completed: { title: 'تکمیل‌شده' },
  cancelled: { title: 'لغوشده' },
};

const getAppointmentDisplayStatus = (apt: Appointment): string => {
  if (apt.status === 'cancelled') return 'cancelled';
  if (apt.status === 'completed') return 'completed';
  if (apt.status === 'in_progress' && apt.step_completed === 2) return 'in_progress';
  if (apt.step_completed < 2) return 'pending';
  return apt.status;
};

const getStatusBadge = (status: string) => {
  const badges: Record<string, { bg: string; text: string; label: string }> = {
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'در انتظار' },
    in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'در حال انجام' },
    completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'تکمیل شده' },
    cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'لغو شده' },
  };
  const badge = badges[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${badge.bg} ${badge.text}`}>
      {badge.label}
    </span>
  );
};

const AdminReports: React.FC = () => {
  const navigate = useNavigate();
  const serviceDonutMaskId = useId().replace(/:/g, '');
  const [dateFrom, setDateFrom] = useState(() => defaultJalaliWeekRange().from);
  const [dateTo, setDateTo] = useState(() => defaultJalaliWeekRange().to);
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [staffOptions, setStaffOptions] = useState<ReportStaffOption[]>([]);
  const [summary, setSummary] = useState<ManagementSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [cancelReasonCsvLoading, setCancelReasonCsvLoading] = useState(false);
  const [showReportCardModal, setShowReportCardModal] = useState(false);
  const [selectedReportCardFilter, setSelectedReportCardFilter] = useState<ReportCardFilter | null>(
    null
  );
  const [reportCardAppointments, setReportCardAppointments] = useState<Appointment[]>([]);
  const [reportCardLoading, setReportCardLoading] = useState(false);
  const [reportCardPage, setReportCardPage] = useState(1);
  const [reportCardTotal, setReportCardTotal] = useState(0);
  const [showAppointmentDetailModal, setShowAppointmentDetailModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [appointmentDetailLoading, setAppointmentDetailLoading] = useState(false);

  useEffect(() => {
    if (!canAccessManagementReport()) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    const loadStaffOptions = async () => {
      try {
        const apiBaseUrl = getApiBaseUrl();
        const res = await fetch(`${apiBaseUrl}/admin/reports/staff-options`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (res.ok) {
          setStaffOptions(await res.json());
        }
      } catch {
        setStaffOptions([]);
      }
    };

    void loadStaffOptions();
  }, []);

  const buildReportQuery = useCallback((): string | null => {
    const fromG = jalaliToGregorianYmd(dateFrom);
    const toG = jalaliToGregorianYmd(dateTo);
    if (!fromG || !toG) {
      return null;
    }
    if (fromG > toG) {
      return null;
    }
    const params = new URLSearchParams({
      from_date: fromG,
      to_date: toG,
    });
    if (selectedAdminId) {
      params.set('admin_id', selectedAdminId);
    }
    return params.toString();
  }, [dateFrom, dateTo, selectedAdminId]);

  const fetchSummary = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    const qs = buildReportQuery();
    if (!qs) {
      setError('تاریخ «از» و «تا» را درست انتخاب کنید؛ تاریخ شروع نباید بعد از پایان باشد.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const apiBaseUrl = getApiBaseUrl();
      const res = await fetch(
        `${apiBaseUrl}/admin/reports/management-summary?${qs}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!res.ok) {
        let msg = 'خطا در دریافت گزارش';
        try {
          const j = await res.json();
          if (typeof j?.detail === 'string') msg = j.detail;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      const data: ManagementSummary = await res.json();
      setSummary(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'خطای ناشناخته');
    } finally {
      setLoading(false);
    }
  }, [buildReportQuery, navigate]);

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  const downloadCsv = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;
    const qs = buildReportQuery();
    if (!qs) {
      setError('تاریخ «از» و «تا» را درست انتخاب کنید؛ تاریخ شروع نباید بعد از پایان باشد.');
      return;
    }
    setExporting(true);
    try {
      const apiBaseUrl = getApiBaseUrl();
      const res = await fetch(`${apiBaseUrl}/admin/reports/management-export?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let msg = 'خطا در دانلود فایل';
        try {
          const j = await res.json();
          if (typeof j?.detail === 'string') msg = j.detail;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fromG = jalaliToGregorianYmd(dateFrom);
      const toG = jalaliToGregorianYmd(dateTo);
      a.download =
        fromG && toG ? `گزارش_نوبت‌ها_${fromG}_${toG}.csv` : 'گزارش_نوبت‌ها.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'خطا در خروجی');
    } finally {
      setExporting(false);
    }
  };

  const downloadCancelReasonsCsv = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;
    setCancelReasonCsvLoading(true);
    setError(null);
    try {
      const apiBaseUrl = getApiBaseUrl();
      const res = await fetch(`${apiBaseUrl}/admin/reports/cancel-reasons-export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let msg = 'خطا در دانلود فایل دلایل لغو';
        try {
          const j = await res.json();
          if (typeof j?.detail === 'string') msg = j.detail;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'دلایل_لغو_نوبت‌ها.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'خطا در خروجی');
    } finally {
      setCancelReasonCsvLoading(false);
    }
  };

  const fetchReportCardAppointments = async (filter: ReportCardFilter, page: number = 1) => {
    const qs = buildReportQuery();
    if (!qs) {
      setError('تاریخ «از» و «تا» را درست انتخاب کنید؛ تاریخ شروع نباید بعد از پایان باشد.');
      return;
    }

    try {
      setReportCardLoading(true);
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const skip = (page - 1) * REPORT_CARD_LIST_PAGE_SIZE;
      const params = new URLSearchParams(qs);
      params.set('card_filter', filter);
      params.set('skip', String(skip));
      params.set('limit', String(REPORT_CARD_LIST_PAGE_SIZE));

      const apiBaseUrl = getApiBaseUrl();
      const res = await fetch(`${apiBaseUrl}/admin/reports/management-appointments?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (res.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!res.ok) {
        throw new Error('خطا در دریافت لیست نوبت‌ها');
      }

      const data: Appointment[] = await res.json();
      const totalHeader = res.headers.get('X-Total-Count');
      const parsed = totalHeader != null && totalHeader !== '' ? parseInt(totalHeader, 10) : NaN;
      const total = Number.isFinite(parsed) ? parsed : data.length;

      setReportCardAppointments(data);
      setReportCardTotal(total);
      setReportCardPage(page);
    } catch {
      setReportCardAppointments([]);
      setReportCardTotal(0);
    } finally {
      setReportCardLoading(false);
    }
  };

  const openReportCardModal = (filter: ReportCardFilter) => {
    setSelectedReportCardFilter(filter);
    setShowReportCardModal(true);
    setReportCardPage(1);
    void fetchReportCardAppointments(filter, 1);
  };

  const closeReportCardModal = () => {
    setShowReportCardModal(false);
    setSelectedReportCardFilter(null);
    setReportCardAppointments([]);
    setReportCardTotal(0);
    setReportCardPage(1);
  };

  const openAppointmentDetail = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setAppointmentDetailLoading(false);
    setShowAppointmentDetailModal(true);
  };

  const closeAppointmentDetailModal = () => {
    setShowAppointmentDetailModal(false);
    setSelectedAppointment(null);
    setAppointmentDetailLoading(false);
  };

  const handleViewFullAppointment = (id: number) => {
    closeAppointmentDetailModal();
    closeReportCardModal();
    navigate(`/admin/appointments/${id}`);
  };

  const reportCardTotalPages = Math.max(
    1,
    Math.ceil(reportCardTotal / REPORT_CARD_LIST_PAGE_SIZE)
  );

  if (loading && !summary) {
    return (
      <div
        className="flex items-center justify-center min-h-[320px] text-lg text-gray-700"
        style={{ direction: 'rtl' }}
      >
        <LoadingSpinner text="در حال بارگذاری گزارش..." />
      </div>
    );
  }

  const maxDayCount =
    summary?.by_day.length && summary.by_day.length > 0
      ? Math.max(...summary.by_day.map((d) => d.count), 1)
      : 1;

  return (
    <div className="space-y-8 text-base text-gray-900" style={{ direction: 'rtl' }}>
      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex min-w-0 w-full max-w-full flex-col gap-5 lg:max-w-none">
          <div className="w-full rounded-xl border border-gray-100 bg-gray-50/80 p-4 sm:p-5">
            <div className="flex w-full flex-col gap-5 lg:grid lg:min-h-0 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:gap-8">
              <div className="min-w-0 w-full lg:justify-self-start">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 sm:gap-x-5">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium text-gray-700">از تاریخ</span>
                    <PersianDatePicker value={dateFrom} onChange={setDateFrom} placeholder="از" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium text-gray-700">تا تاریخ</span>
                    <PersianDatePicker value={dateTo} onChange={setDateTo} placeholder="تا" />
                  </div>
                  <div className="flex flex-col gap-1.5 sm:col-span-2 lg:col-span-1">
                    <span className="text-sm font-medium text-gray-700">کارشناس</span>
                    <select
                      value={selectedAdminId}
                      onChange={(e) => setSelectedAdminId(e.target.value)}
                      className="select-rtl w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[46px]"
                    >
                      <option value="">همه کارشناسان</option>
                      {staffOptions.map((staff) => (
                        <option key={staff.admin_id} value={String(staff.admin_id)}>
                          {staff.username}
                          {staff.role ? ` (${adminRoleFa(staff.role)})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex w-full shrink-0 flex-col items-end gap-2.5 sm:flex-row sm:flex-wrap sm:items-center lg:w-auto lg:justify-self-end">
                <div className="group relative w-full max-w-[13.5rem] sm:max-w-none sm:w-auto sm:min-w-[10.5rem]">
                  <button
                    type="button"
                    onClick={() => void downloadCsv()}
                    disabled={exporting}
                    aria-describedby="excel-export-hint"
                    className={`${reportToolbarBtnClass} w-full bg-gradient-to-b from-blue-600 to-blue-700 text-white shadow-md ring-1 ring-blue-500/30 hover:from-blue-500 hover:to-blue-600 hover:shadow-lg active:scale-[0.98] sm:w-auto`}
                  >
                    {exporting ? 'در حال آماده‌سازی…' : 'دانلود اکسل'}
                  </button>
                  <span id="excel-export-hint" className="sr-only">
                    {EXCEL_EXPORT_HINT}
                  </span>
                  <span role="tooltip" aria-hidden="true" className={toolbarTooltipBubbleClass}>
                    {EXCEL_EXPORT_HINT}
                  </span>
                </div>
                <div className="group relative w-full max-w-[13.5rem] sm:max-w-none sm:w-auto sm:min-w-[10.5rem]">
                  <button
                    type="button"
                    onClick={() => void downloadCancelReasonsCsv()}
                    disabled={cancelReasonCsvLoading}
                    aria-describedby="cancel-reasons-export-hint"
                    className={`${reportToolbarBtnClass} w-full border border-rose-200/90 bg-gradient-to-b from-rose-50 to-rose-100/90 text-rose-900 shadow-sm ring-1 ring-rose-200/50 hover:border-rose-300 hover:from-rose-50 hover:to-rose-100 hover:shadow active:scale-[0.98] sm:w-auto`}
                  >
                    {cancelReasonCsvLoading ? 'در حال آماده‌سازی…' : 'خروجی دلایل لغو'}
                  </button>
                  <span id="cancel-reasons-export-hint" className="sr-only">
                    {CANCEL_REASONS_EXPORT_HINT}
                  </span>
                  <span role="tooltip" aria-hidden="true" className={toolbarTooltipBubbleClass}>
                    {CANCEL_REASONS_EXPORT_HINT}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-base text-red-900 leading-relaxed">
          {error}
        </div>
      )}

      {summary && (
        <>
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
              <span className="font-medium text-gray-500">از</span>
              <span className="font-semibold tabular-nums text-gray-900">{dateFrom}</span>
              <span className="text-gray-300">·</span>
              <span className="font-medium text-gray-500">تا</span>
              <span className="font-semibold tabular-nums text-gray-900">{dateTo}</span>
            </div>
            {summary.filtered_admin && (
              <>
                <span className="hidden sm:inline text-gray-200">|</span>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                  {summary.filtered_admin.username}
                </span>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {(
              [
                {
                  filterKey: 'total' as const,
                  label: 'کل',
                  value: summary.appointments.total_registered,
                  bar: 'bg-indigo-500',
                  surface:
                    'border-indigo-100/80 bg-gradient-to-br from-indigo-50/90 via-white to-white ring-indigo-100/60',
                },
                {
                  filterKey: 'open' as const,
                  label: 'سفارشات باز',
                  value: summary.open_orders,
                  bar: 'bg-amber-500',
                  surface:
                    'border-amber-100/80 bg-gradient-to-br from-amber-50/90 via-white to-white ring-amber-100/60',
                },
                {
                  filterKey: 'completed' as const,
                  label: 'تکمیل‌شده',
                  value: summary.appointments.completed,
                  bar: 'bg-emerald-500',
                  surface:
                    'border-emerald-100/80 bg-gradient-to-br from-emerald-50/90 via-white to-white ring-emerald-100/60',
                },
                {
                  filterKey: 'cancelled' as const,
                  label: 'لغوشده',
                  value: summary.appointments.cancelled,
                  bar: 'bg-rose-500',
                  surface:
                    'border-rose-100/80 bg-gradient-to-br from-rose-50/90 via-white to-white ring-rose-100/60',
                },
              ] as const
            ).map((card) => (
              <button
                type="button"
                key={card.label}
                onClick={() => openReportCardModal(card.filterKey)}
                className={`relative overflow-hidden rounded-2xl border p-5 text-right shadow-md shadow-gray-200/30 ring-1 transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 active:scale-[0.99] sm:p-6 ${card.surface}`}
              >
                <div className={`absolute inset-x-0 top-0 h-1 ${card.bar}`} aria-hidden />
                <p className="text-sm font-medium text-gray-600 sm:text-base">{card.label}</p>
                <p className="mt-3 text-3xl font-bold tabular-nums tracking-tight text-gray-900 sm:text-4xl">
                  {card.value.toLocaleString('fa-IR')}
                </p>
              </button>
            ))}
          </div>

          {summary.expert_insights?.rankings && (
            <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
              <div className="relative z-20 overflow-visible border-b border-gray-100 px-5 pb-6 pt-5 sm:px-8 sm:pb-7 sm:pt-6">
                <div
                  className="group relative w-full cursor-help rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  tabIndex={0}
                >
                  <div className="inline-flex max-w-full flex-wrap items-center gap-2">
                    <h2
                      className="text-xl font-bold text-gray-900 sm:text-2xl"
                      aria-describedby="expert-insights-hint"
                    >
                      عملکرد کارشناسان
                    </h2>
                    <span
                      className="inline-flex h-6 min-w-[1.5rem] select-none items-center justify-center rounded-full border border-gray-200 bg-gray-50 px-1.5 text-[10px] font-bold leading-none text-gray-500"
                      aria-hidden
                    >
                      i
                    </span>
                  </div>
                  <span id="expert-insights-hint" className="sr-only">
                    {EXPERT_INSIGHTS_HINT}
                  </span>
                  <span role="tooltip" aria-hidden="true" className={toolbarTooltipBelowClass}>
                    {EXPERT_INSIGHTS_HINT}
                  </span>
                </div>
              </div>
              <div className="rounded-b-xl p-5 sm:p-8">
                {(
                  [
                    {
                      key: 'activity',
                      title: 'فعال‌ترین کارشناسان (به ترتیب)',
                      accent: 'border-blue-200',
                      headClass: 'bg-blue-50/90',
                      rows: summary.expert_insights.rankings.by_activity,
                      empty: 'در این بازه لاگ فعالیتی برای نوبت ثبت نشده است.',
                      renderRow: (row: ExpertRankingActivity, rank: number) => {
                        const stats = expertStatsByAdminId(
                          summary.expert_insights!.rankings,
                          row.admin_id
                        );
                        return (
                          <tr
                            key={row.admin_id}
                            className={rank === 1 ? 'bg-amber-50/80' : 'bg-white even:bg-gray-50/60'}
                          >
                            <td className="px-2 py-2.5 text-sm font-bold text-gray-900 tabular-nums whitespace-nowrap sm:px-3 sm:py-3 sm:text-base">
                              {rank.toLocaleString('fa-IR')}
                            </td>
                            <td className="px-2 py-2.5 text-sm font-semibold text-gray-900 break-words sm:px-3 sm:py-3 sm:text-base">
                              {row.username}
                            </td>
                            <td className="px-2 py-2.5 text-sm text-gray-700 whitespace-nowrap sm:px-3 sm:py-3">
                              {adminRoleFa(row.role)}
                            </td>
                            <td className="px-2 py-2.5 text-center text-sm font-semibold tabular-nums text-blue-800 sm:px-3 sm:py-3">
                              {(row.actions_share_pct ?? 0).toLocaleString('fa-IR', {
                                maximumFractionDigits: 1,
                              })}
                              ٪
                            </td>
                            <td className="px-2 py-2.5 text-center text-sm tabular-nums text-gray-700 sm:px-3 sm:py-3">
                              {row.distinct_appointments_touched.toLocaleString('fa-IR')}
                            </td>
                            <td className="px-2 py-2.5 text-center sm:px-3 sm:py-3">
                              <div className="flex flex-col items-center gap-0.5 leading-tight">
                                <span className="text-sm font-semibold tabular-nums text-emerald-700">
                                  {stats.completions.toLocaleString('fa-IR')}
                                </span>
                                <span className="text-xs tabular-nums text-emerald-600/90">
                                  {formatExpertPct(stats.completionPct)}٪
                                </span>
                              </div>
                            </td>
                            <td className="px-2 py-2.5 text-center sm:px-3 sm:py-3">
                              <div className="flex flex-col items-center gap-0.5 leading-tight">
                                <span className="text-sm font-semibold tabular-nums text-rose-700">
                                  {stats.cancellations.toLocaleString('fa-IR')}
                                </span>
                                <span className="text-xs tabular-nums text-rose-600/90">
                                  {formatExpertPct(stats.cancellationPct)}٪
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      },
                      thead: (
                        <tr>
                          <TableHeaderWithTooltip label="رتبه" hint={EXPERT_ACTIVITY_COLUMN_HINTS.rank} />
                          <TableHeaderWithTooltip label="نام کاربری" hint={EXPERT_ACTIVITY_COLUMN_HINTS.username} />
                          <TableHeaderWithTooltip label="نقش" hint={EXPERT_ACTIVITY_COLUMN_HINTS.role} />
                          <TableHeaderWithTooltip
                            label="٪ از کل اقدامات"
                            hint={EXPERT_ACTIVITY_COLUMN_HINTS.share}
                            align="center"
                          />
                          <TableHeaderWithTooltip
                            label="نوبت"
                            hint={EXPERT_ACTIVITY_COLUMN_HINTS.appointments}
                            align="center"
                          />
                          <TableHeaderWithTooltip
                            label="تکمیل شده"
                            hint={EXPERT_ACTIVITY_COLUMN_HINTS.completed}
                            align="center"
                          />
                          <TableHeaderWithTooltip
                            label="لغو شده"
                            hint={EXPERT_ACTIVITY_COLUMN_HINTS.cancelled}
                            align="center"
                          />
                        </tr>
                      ),
                    },
                  ] as const
                ).map((section) => (
                  <div
                    key={section.key}
                    className={`rounded-xl border-2 shadow-sm ${section.accent}`}
                  >
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 px-4 py-3 sm:px-5 border-b border-gray-200 bg-white/90">
                      {section.title}
                    </h3>
                    {section.rows.length === 0 ? (
                      <p className="text-gray-600 text-base px-4 py-8 sm:px-5 bg-white">{section.empty}</p>
                    ) : (
                      <div className="overflow-x-auto bg-white sm:overflow-x-visible">
                        <table className="w-full min-w-[38rem] table-auto divide-y divide-gray-200 text-right sm:min-w-0">
                          <thead className={section.headClass}>
                            {section.thead}
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {section.rows.map((row, idx) =>
                              section.renderRow(row as never, idx + 1)
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="border-b border-gray-100 px-6 py-5 sm:px-8">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">تفکیک روزانهٔ ثبت نوبت</h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-gray-500">
                    تفکیک وضعیت ثبت در هر روز شمسی
                    {summary.by_day.length > DAILY_CHART_SCROLL_THRESHOLD
                      ? ' · برای روزهای بیشتر افقی بکشید'
                      : ''}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-600 sm:justify-end">
                  {DAILY_CHART_SEGMENTS.map((seg) => (
                    <span key={seg.key} className="inline-flex items-center gap-1.5">
                      <span className={`h-2.5 w-2.5 rounded-sm shadow-sm ${seg.barClass}`} aria-hidden />
                      {seg.label}
                    </span>
                  ))}
                  {summary.by_day.length > 0 && (
                    <span className="mr-auto rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium tabular-nums text-gray-700 sm:mr-0">
                      بیشترین: {maxDayCount.toLocaleString('fa-IR')}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {summary.by_day.length === 0 ? (
              <p className="px-6 py-12 text-center text-base text-gray-600 sm:px-8">
                در این بازه داده‌ای برای نمودار روزانه وجود ندارد.
              </p>
            ) : (
              (() => {
                const dayCount = summary.by_day.length;
                const useScrollLayout = dayCount > DAILY_CHART_SCROLL_THRESHOLD;
                return (
                  <div className="px-6 py-5 sm:px-8 sm:py-6">
                    {useScrollLayout ? (
                      <div
                        className="flex snap-x snap-mandatory flex-nowrap items-end gap-3 overflow-x-auto pb-1 [scrollbar-width:thin]"
                        style={{ direction: 'ltr' }}
                      >
                        {summary.by_day.map((d) => (
                          <DailyBreakdownBar
                            key={d.date}
                            day={d}
                            maxDayCount={maxDayCount}
                            compact
                          />
                        ))}
                      </div>
                    ) : (
                      <div
                        className="grid items-end gap-1 sm:gap-2"
                        style={{
                          gridTemplateColumns: `repeat(${dayCount}, minmax(0, 1fr))`,
                        }}
                      >
                        {summary.by_day.map((d) => (
                          <DailyBreakdownBar
                            key={d.date}
                            day={d}
                            maxDayCount={maxDayCount}
                            compact={false}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()
            )}
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="flex flex-col gap-2 border-b border-gray-100 px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8">
              <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">تفکیک بر اساس خدمت</h2>
              {summary.by_service.length > 0 && (
                <p className="text-sm tabular-nums text-gray-600">
                  جمع نوبت‌ها:{' '}
                  <span className="font-bold text-gray-900">
                    {summary.by_service
                      .reduce((acc, r) => acc + r.count, 0)
                      .toLocaleString('fa-IR')}
                  </span>
                </p>
              )}
            </div>
            <div className="bg-gradient-to-b from-slate-50/60 to-white px-4 py-8 sm:px-8 sm:py-10">
              {summary.by_service.length === 0 ? (
                <p className="py-8 text-center text-base text-gray-600 sm:text-lg">
                  در این بازه داده‌ای برای تفکیک خدمت وجود ندارد.
                </p>
              ) : (
                (() => {
                  const totalSvc = summary.by_service.reduce((acc, r) => acc + r.count, 0);
                  const cx = 100;
                  const cy = 100;
                  const rOut = 88;
                  const rIn = 54;
                  const start0 = -Math.PI / 2;
                  let acc = start0;
                  const slices = summary.by_service.map((row, idx) => {
                    const sweep =
                      totalSvc > 0 ? (row.count / totalSvc) * 2 * Math.PI : 0;
                    const start = acc;
                    acc += sweep;
                    const d = donutSlicePath(cx, cy, rOut, rIn, start, sweep);
                    const fill = SERVICE_PIE_FILLS[idx % SERVICE_PIE_FILLS.length];
                    return { row, idx, d, fill };
                  });
                  const nonZero = summary.by_service.filter((r) => r.count > 0);
                  const solidRing =
                    nonZero.length === 1 && totalSvc > 0 && nonZero[0].count === totalSvc;
                  const solidFillIx = summary.by_service.findIndex((r) => r.count > 0);
                  const solidFill =
                    solidFillIx >= 0
                      ? SERVICE_PIE_FILLS[solidFillIx % SERVICE_PIE_FILLS.length]
                      : SERVICE_PIE_FILLS[0];

                  return (
                    <div className="flex flex-col items-center gap-10 lg:flex-row lg:items-center lg:justify-center lg:gap-14">
                      <div className="relative shrink-0 drop-shadow-md">
                        <svg
                          className="h-64 w-64 sm:h-72 sm:w-72"
                          viewBox="0 0 200 200"
                          role="img"
                          aria-label="نمودار دایره‌ای سهم خدمات از نوبت‌ها"
                        >
                          <defs>
                            <mask id={serviceDonutMaskId}>
                              <rect width="200" height="200" fill="white" />
                              <circle cx={cx} cy={cy} r={rIn} fill="black" />
                            </mask>
                          </defs>
                          {solidRing ? (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={rOut}
                              fill={solidFill}
                              mask={`url(#${serviceDonutMaskId})`}
                              className="transition-opacity hover:opacity-95"
                            />
                          ) : (
                            slices.map(({ row, idx, d, fill }) =>
                              d ? (
                                <path
                                  key={`${row.service_id}-${row.service_name}-${idx}`}
                                  d={d}
                                  fill={fill}
                                  stroke="white"
                                  strokeWidth="2"
                                  className="transition-opacity hover:opacity-92"
                                >
                                  <title>
                                    {row.service_name}: {row.count.toLocaleString('fa-IR')} نوبت
                                  </title>
                                </path>
                              ) : null
                            )
                          )}
                          <text
                            x={cx}
                            y={cy - 6}
                            textAnchor="middle"
                            className="fill-gray-500 text-[11px] font-medium"
                            style={{ fontFamily: 'inherit' }}
                          >
                            جمع
                          </text>
                          <text
                            x={cx}
                            y={cy + 16}
                            textAnchor="middle"
                            className="fill-gray-900 text-[19px] font-bold tabular-nums"
                            style={{ fontFamily: 'inherit' }}
                          >
                            {totalSvc.toLocaleString('fa-IR')}
                          </text>
                        </svg>
                      </div>
                      <ul className="w-full max-w-md space-y-2.5 lg:max-w-lg">
                        {summary.by_service.map((row, idx) => {
                          const fill = SERVICE_PIE_FILLS[idx % SERVICE_PIE_FILLS.length];
                          const sharePct = totalSvc > 0 ? (row.count / totalSvc) * 100 : 0;
                          return (
                            <li
                              key={`${row.service_id}-${row.service_name}-legend`}
                              className="flex items-center gap-3 rounded-xl border border-gray-100/90 bg-white/90 px-4 py-3 shadow-sm"
                            >
                              <span
                                className="h-4 w-4 shrink-0 rounded-full ring-2 ring-white shadow-sm"
                                style={{ backgroundColor: fill }}
                                aria-hidden
                              />
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold leading-snug text-gray-900 break-words">
                                  {row.service_name}
                                </p>
                                <p className="mt-0.5 text-sm tabular-nums text-gray-600">
                                  {sharePct.toLocaleString('fa-IR', { maximumFractionDigits: 1 })}٪ از کل
                                </p>
                              </div>
                              <p className="shrink-0 text-lg font-bold tabular-nums text-gray-900">
                                {row.count.toLocaleString('fa-IR')}
                              </p>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        </>
      )}

      {showReportCardModal && selectedReportCardFilter && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
          style={{ direction: 'rtl' }}
          onClick={closeReportCardModal}
        >
          <div
            className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-xl border border-gray-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {REPORT_CARD_CONFIG[selectedReportCardFilter].title}
                </h3>
                <p className="mt-0.5 text-sm text-gray-500">
                  {reportCardTotal.toLocaleString('fa-IR')} نوبت · بازه {dateFrom} تا {dateTo}
                </p>
              </div>
              <button
                type="button"
                onClick={closeReportCardModal}
                className="p-1 text-xl leading-none text-gray-400 hover:text-gray-600"
                aria-label="بستن"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {reportCardLoading ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner text="در حال دریافت نوبت‌ها..." />
                </div>
              ) : reportCardAppointments.length === 0 ? (
                <p className="py-8 text-center text-gray-500">نوبتی در این دسته یافت نشد.</p>
              ) : (
                <div className="space-y-2">
                  {reportCardAppointments.map((apt) => (
                    <button
                      key={apt.id}
                      type="button"
                      onClick={() => openAppointmentDetail(apt)}
                      className="flex w-full items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4 text-right transition-all hover:border-blue-200 hover:bg-blue-50/50"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex flex-wrap items-center gap-2">
                          <p className="truncate font-semibold text-gray-900">
                            {apt.user?.full_name || 'کاربر'}
                          </p>
                          {getStatusBadge(getAppointmentDisplayStatus(apt))}
                        </div>
                        <p className="truncate text-sm text-gray-600">
                          {apt.service?.name || 'نامشخص'}
                        </p>
                        {apt.cancel_reason?.trim() && (
                          <p className="mt-1 truncate text-xs text-red-700">
                            دلیل لغو: {apt.cancel_reason.trim()}
                          </p>
                        )}
                        {apt.cancelled_by_username?.trim() && (
                          <p className="mt-0.5 truncate text-xs text-gray-600">
                            لغو کننده: {apt.cancelled_by_username.trim()}
                          </p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          {new Date(apt.created_at).toLocaleString('fa-IR', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                      <div className="shrink-0 text-left">
                        <p className="rounded-lg bg-blue-50 px-2.5 py-1 text-sm font-semibold text-blue-700">
                          #{apt.tracking_code || 1000 + apt.id}
                        </p>
                        {apt.appointment_date && (
                          <p className="mt-1 text-xs text-gray-500">
                            {apt.appointment_date}
                            {apt.appointment_time ? ` — ${apt.appointment_time}` : ''}
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {reportCardTotalPages > 1 && !reportCardLoading && (
              <div className="flex shrink-0 items-center justify-between border-t border-gray-200 px-5 py-4">
                <button
                  type="button"
                  disabled={reportCardPage <= 1}
                  onClick={() =>
                    selectedReportCardFilter &&
                    void fetchReportCardAppointments(selectedReportCardFilter, reportCardPage - 1)
                  }
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-40"
                >
                  قبلی
                </button>
                <span className="text-sm text-gray-600">
                  صفحه {reportCardPage.toLocaleString('fa-IR')} از{' '}
                  {reportCardTotalPages.toLocaleString('fa-IR')}
                </span>
                <button
                  type="button"
                  disabled={reportCardPage >= reportCardTotalPages}
                  onClick={() =>
                    selectedReportCardFilter &&
                    void fetchReportCardAppointments(selectedReportCardFilter, reportCardPage + 1)
                  }
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-40"
                >
                  بعدی
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showAppointmentDetailModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={closeAppointmentDetailModal}
          style={{ direction: 'ltr' }}
        >
          <div
            className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{ direction: 'rtl' }}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-xl border-b border-gray-100 bg-white px-6 py-4">
              <h3 className="text-lg font-bold text-gray-900">
                {appointmentDetailLoading
                  ? 'خلاصه نوبت'
                  : `خلاصه نوبت #${selectedAppointment?.tracking_code || (selectedAppointment ? 1000 + selectedAppointment.id : '')}`}
              </h3>
              <button
                type="button"
                onClick={closeAppointmentDetailModal}
                className="p-1 text-xl leading-none text-gray-400 hover:text-gray-600"
                aria-label="بستن"
              >
                ×
              </button>
            </div>

            {appointmentDetailLoading ? (
              <div className="flex justify-center py-16">
                <LoadingSpinner text="در حال دریافت جزئیات نوبت..." />
              </div>
            ) : selectedAppointment ? (
              <>
                <div className="space-y-4 p-6">
                  <div className="flex items-center gap-2">
                    {getStatusBadge(getAppointmentDisplayStatus(selectedAppointment))}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        نام و نام خانوادگی
                      </label>
                      <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-900">
                        {selectedAppointment.user?.full_name || '-'}
                      </p>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">شماره تماس</label>
                      <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-900" dir="ltr">
                        {selectedAppointment.user?.phone || '-'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">خدمت</label>
                      <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-900">
                        {selectedAppointment.service?.name || 'نامشخص'}
                      </p>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">تاریخ مراجعه</label>
                      <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-900">
                        {selectedAppointment.appointment_date || '-'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">ساعت مراجعه</label>
                      <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-900">
                        {selectedAppointment.appointment_time || '-'}
                      </p>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">تاریخ ثبت</label>
                      <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-900">
                        {new Date(selectedAppointment.created_at).toLocaleString('fa-IR', {
                          timeZone: 'Asia/Tehran',
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>

                  {selectedAppointment.cancel_reason?.trim() && (
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">دلیل لغو</label>
                      <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
                        {selectedAppointment.cancel_reason.trim()}
                      </p>
                    </div>
                  )}

                  {selectedAppointment.cancelled_by_username?.trim() && (
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">لغو کننده</label>
                      <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-900">
                        {selectedAppointment.cancelled_by_username.trim()}
                      </p>
                    </div>
                  )}
                </div>

                <div className="sticky bottom-0 flex justify-end gap-3 rounded-b-xl border-t border-gray-100 bg-gray-50 px-6 py-4">
                  <button
                    type="button"
                    onClick={closeAppointmentDetailModal}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    بستن
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewFullAppointment(selectedAppointment.id)}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
                  >
                    مشاهده جزئیات کامل
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReports;
